import type { ChatState, ChatStateNotice } from "../../types"
import { jidLocalPart, type Jid } from "../../xmpp/jid"
import type { ChatSession } from "../session"
import type { ChatStore } from "../store"

/**
 * How long someone shows as typing without hearing from them again. A sender
 * that never says `paused` — a closed tab, a dropped socket — would otherwise
 * type forever. Senders refresh well inside it (`createTypingNotifier`).
 */
export const TYPING_EXPIRY_MS = 30_000

const expiries = new Map<string, ReturnType<typeof setTimeout>>()
const expiryKey = (peer: Jid, author: string) => `${peer} ${author}`

// ─── What the client calls ──────────────────────────────────────────────────

/** Tell a conversation what we are doing in its composer. */
export function sendChatState(
  session: ChatSession,
  store: ChatStore,
  peer: Jid,
  state: ChatState
) {
  if (!session.isReady() || store.getState().connState !== "online") return

  session.client.sendChatState(peer, state)
}

export function onChatState(
  session: ChatSession,
  store: ChatStore,
  { state, author, routing }: ChatStateNotice
) {
  if (!session.isReady() || !author) return

  // Our own, echoed by the room or carbon-copied from another tab.
  if (author === jidLocalPart(session.selfJid)) return

  // Anyone else's arrives from the conversation: the peer, or the room.
  const peer = routing.from

  if (state === "composing") startTyping(store, peer, author)
  else stopTyping(store, peer, author)
}

// ─── Writers ────────────────────────────────────────────────────────────────

function startTyping(store: ChatStore, peer: Jid, author: string) {
  const key = expiryKey(peer, author)
  clearTimeout(expiries.get(key))
  expiries.set(
    key,
    setTimeout(() => stopTyping(store, peer, author), TYPING_EXPIRY_MS)
  )

  const typing = store.getState().typing[peer] ?? []
  if (typing.includes(author)) return

  store.setState({
    typing: { ...store.getState().typing, [peer]: [...typing, author] },
  })
}

/** Also called when their message lands: it is what they were typing. */
export function stopTyping(store: ChatStore, peer: Jid, author: string) {
  const key = expiryKey(peer, author)
  clearTimeout(expiries.get(key))
  expiries.delete(key)

  const typing = store.getState().typing[peer]
  if (!typing?.includes(author)) return

  const rest = typing.filter((handle) => handle !== author)
  store.setState({
    typing: {
      ...store.getState().typing,
      [peer]: rest.length > 0 ? rest : undefined,
    },
  })
}
